import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api.js'
import { useAuth } from '../store/authStore.js'
import { ArrowRight, CheckCircle2, Lock, FileText } from 'lucide-react'

export default function Dashboard() {
  const user = useAuth(s => s.user)
  const [items, setItems] = useState(null)

  useEffect(() => {
    api.myResponses().then(setItems).catch(() => setItems([]))
  }, [])

  if (!items) return <div className="text-ink-soft">Lade…</div>

  return (
    <div className="space-y-8">
      <div className="max-w-[760px]">
        <p className="text-xs uppercase tracking-[0.14em] font-semibold text-teal mb-3.5">Übersicht</p>
        <h1 className="text-[44px] leading-[1.05] tracking-[-0.025em] font-bold">Willkommen, {user.firstName}.</h1>
        <p className="mt-3.5 text-base text-ink-soft leading-[1.55]">Hier finden Sie Ihre verfügbaren Werkzeuge zur Selbstreflexion.</p>
      </div>

      {items.length === 0 ? (
        <div className="card p-8 text-center text-ink-soft">
          Noch keine Umfragen zugewiesen. Bitte wenden Sie sich an Ihre Administration.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map(item => (
            <SurveyCard key={item.accessId} item={item} />
          ))}
        </div>
      )}
    </div>
  )
}

function SurveyCard({ item }) {
  const isCompleted = !!item.completedAt
  const isLocked = item.locked
  const inProgress = item.response && item.response.status === 'in_progress' && !isCompleted

  const status = isLocked ? 'gesperrt' : isCompleted ? 'abgeschlossen' : 'offen'
  const target = isCompleted
    ? `/survey/${item.accessId}/result`
    : !isLocked ? `/survey/${item.accessId}` : null
  const ctaLabel = isCompleted ? 'Ergebnis ansehen' : inProgress ? 'Fortsetzen' : 'Starten'
  const footerLeft = isCompleted
    ? `Beendet am ${new Date(item.completedAt).toLocaleDateString('de-DE')}`
    : inProgress
      ? `Schritt ${item.response.currentStep}`
      : 'Bereit zum Start'

  return (
    <article className="bg-white border border-[#EAEBEF] rounded-[14px] p-6 flex flex-col min-h-[240px] shadow-card transition hover:-translate-y-0.5 hover:border-[#DDE1E8] hover:shadow-[0_4px_8px_rgba(15,30,40,0.06),0_18px_40px_-16px_rgba(15,30,40,0.22)]">
      <header className="flex items-start justify-between gap-3 mb-6">
        <div className="w-[42px] h-[42px] rounded-[10px] bg-[#E6F0F2] text-teal grid place-items-center">
          <FileText className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <StatusBadge status={status} />
      </header>

      <div className="flex-1">
        <p className="font-mono text-[11px] text-ink-mute tracking-wide mb-1.5">v{item.survey.version}</p>
        <h3 className="text-[17px] leading-[1.3] tracking-[-0.01em] font-semibold">{item.survey.name}</h3>
      </div>

      <footer className="border-t border-[#EAEBEF] mt-5 pt-3.5 flex items-center justify-between">
        <span className="text-[13px] text-ink-soft">{footerLeft}</span>
        {target ? (
          <Link
            to={target}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-teal hover:text-teal-700 transition"
          >
            {ctaLabel} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
          </Link>
        ) : (
          <span className="text-[13px] text-ink-mute">—</span>
        )}
      </footer>
    </article>
  )
}

function StatusBadge({ status }) {
  const map = {
    abgeschlossen: {
      cls: 'bg-emerald-50 text-emerald-700',
      icon: <CheckCircle2 size={12} strokeWidth={2.2} />,
      label: 'Abgeschlossen',
    },
    offen: {
      cls: 'bg-amber-50 text-amber-700',
      icon: null,
      label: 'Offen',
    },
    gesperrt: {
      cls: 'bg-[#EAEBEF] text-ink-soft',
      icon: <Lock size={12} strokeWidth={2} />,
      label: 'Gesperrt',
    },
  }
  const s = map[status] || map.offen
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-semibold ${s.cls}`}>
      {s.icon}{s.label}
    </span>
  )
}
